import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const HOW_IT_WORKS = [
  {img : 'img/how-it-works-1.png' , heading : 'Apply' , description : 'Tell us about what are you trying to solve'},
  {img : 'img/how-it-works-2.png' , heading : 'Scoping Call' , description : 'Align on your idea, blockers, and growth plan (Week 1)'},
  {img : 'img/how-it-works-3.png' , heading : 'Build & Launch' , description : 'MVP or GTM ready in 4–6 weeks'},
  {img : 'img/how-it-works-4.png' , heading : 'Scale & Raise' , description : 'Introductions to customers/investors post-traction'},
]

const HowItWorksSection = () => {
    const sectionRef = useRef(null);
    const headerRefs = useRef([]); // For H2 and H3
    const cardRefs = useRef([]);   // For the grey cards
    const iconRefs = useRef([]);   // For the white circle images

    useEffect(() => {
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: "top 75%", // Triggers when top of section is 75% down the viewport
                    toggleActions: "play none none none",
                }
            });

            // 1. Headers Fade Up
            tl.from(headerRefs.current, {
                y: 30,
                opacity: 0,
                duration: 0.6,
                stagger: 0.15,
                ease: "power2.out"
            });

            // 2. Cards Pop In (Staggered)
            // This creates the "Step 1 -> Step 2 -> Step 3" flow
            tl.from(cardRefs.current, {
                y: 50,
                opacity: 0,
                duration: 0.6,
                stagger: 0.2, // 0.2s delay between each card
                ease: "back.out(1.7)" // "Back" ease gives it a slight overshoot/pop
            }, "-=0.2");

            // 3. Icons Elastic Pop
            // The icons pop out specifically to draw attention to the visual
            tl.from(iconRefs.current, {
                scale: 0,
                rotation: -45, // Slight rotation for flair
                duration: 0.8,
                stagger: 0.2,
                ease: "elastic.out(1, 0.5)" // Bouncy elastic effect
            }, "-=1.0"); // Start this while cards are still animating

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    // Helper to add refs safely
    const addToHeaderRefs = (el) => { if (el && !headerRefs.current.includes(el)) headerRefs.current.push(el); };
    const addToCardRefs = (el) => { if (el && !cardRefs.current.includes(el)) cardRefs.current.push(el); };
    const addToIconRefs = (el) => { if (el && !iconRefs.current.includes(el)) iconRefs.current.push(el); };

    return (
        <section ref={sectionRef} className='flex flex-col gap-y-5 container max-w-7xl mx-auto px-6 md:px-12 my-32'>
            <h2 
                ref={addToHeaderRefs} 
                className='text-[#111827] text-center font-[600] leading-[40px] text-[34px] sm:text-[40px]'
            >
                How It Works
            </h2>
            <h3 
                ref={addToHeaderRefs} 
                className='text-[#111827] text-center font-[600] text-[18px] sm:text-[23px]'
            >
                India Co-Build Journey A simple 4-step Visual Journey with Timelines
            </h3>
            
            <ol className='grid grid-cols-1 min-[640px]:grid-cols-2 min-[1028px]:grid-cols-4 gap-8'>
                {HOW_IT_WORKS.map((item, index) => (
                    <li key={index} className="h-full">
                        {/* Added transition properties for hover effects after animation */}
                        <article 
                            ref={addToCardRefs}
                            className='flex flex-col gap-y-5 items-center justify-center bg-[#E5E7EB] px-8 py-14 rounded-[15px] h-full transform transition-transform hover:-translate-y-2 duration-300'
                        >
                            <figure 
                                ref={addToIconRefs}
                                className='w-24 h-24 relative bg-[#FFFFFF] rounded-full flex items-center justify-center p-4 shadow-md'
                            >
                                <img src={item.img} className='w-full h-full object-scale-down' alt={item.heading} />
                            </figure>
                            
                            <figcaption className='flex flex-col items-center justify-center gap-y-7 mt-5'>
                                <h4 className='text-center text-[#000000] text-[21px] leading-[20px] font-[600]'>
                                    {item.heading}
                                </h4>
                                <h5 className='text-center text-[#000000] text-[16px] leading-[20px] font-[600]'>
                                    {item.description}
                                </h5>
                            </figcaption>
                        </article>
                    </li>
                ))}
            </ol>
        </section>
    );
};

export default HowItWorksSection;